// InfluencerAnalysis.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  SafeAreaView,
  Linking,
  ActivityIndicator,
  TouchableOpacity,
  Image,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { BarChart, PieChart } from 'react-native-chart-kit';
import { LinearGradient } from 'expo-linear-gradient';

const InfluencerAnalysis = ({ route, navigation }) => {
  const [profileData, setProfileData] = useState(null);
  const [additionalData, setAdditionalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAdditionalData, setLoadingAdditionalData] = useState(true);

  // NEW: session + auth state for sidenav
  const [userData, setUserData] = useState({});
  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState('none'); // "subscriber" | "none"
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === 'subscriber';

  const { PlatformUsername } = route.params;

  const { width } = useWindowDimensions();
  const isLargeLayout = width >= 900;
  const contentWidth = Math.min(width - 32, 1100);
  const chartWidth = isLargeLayout ? contentWidth / 2 - 48 : contentWidth - 40;

  useEffect(() => {
    // Fetch main social profile data
    fetch(
      `https://collabbayadmin.com/APIControllers/SocialProfileDataAPI.php?PlatformUsername=${PlatformUsername}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data && !data.error && data.instagramName) {
          setProfileData(data);
        } else {
          console.error('Unexpected API structure or error:', data);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching Social Profile Data:', error);
        setLoading(false);
      });
  }, [PlatformUsername]);

  useEffect(() => {
    // Fetch additional influencer data
    fetch(
      `https://collabbayadmin.com/APIControllers/InfluencerDataProfile.php?username=${PlatformUsername}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data) {
          setAdditionalData(data);
        }
      })
      .catch((error) =>
        console.error('Error fetching Influencer Data Profile:', error)
      )
      .finally(() => setLoadingAdditionalData(false));
  }, [PlatformUsername]);

  // NEW: fetch session + subscription auth for sidenav
  useEffect(() => {
    // Session data
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.Username) {
          setUserData(data);
        }
      })
      .catch((err) => console.error('Error fetching session data:', err));

    // Subscription/auth state
    fetch(
      'https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php',
      {
        method: 'GET',
        credentials: 'include',
      }
    )
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data.authenticated === 'boolean'
            ? data.authenticated
            : data.status === 'active';
        const mode = data.auth_mode
          ? data.auth_mode
          : data.status === 'active'
          ? 'subscriber'
          : 'none';

        setIsAuthed(!!authed);
        setAuthMode(mode);
      })
      .catch((err) => console.error('Error fetching auth state:', err))
      .finally(() => setAuthChecked(true));
  }, []);

  const handleBackPress = () => {
    navigation.goBack();
  };

  const handleOpenInstagram = () => {
    Linking.openURL(`https://www.instagram.com/${PlatformUsername}`);
  };

  // NEW: open link in same tab (web) or via Linking (native)
  const openInSameTab = (url) => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  // Derive numeric age (then we decide if we show it)
  const numericAge = useMemo(() => {
    if (!additionalData) return null;

    if (additionalData.Age) {
      const parsed = parseInt(additionalData.Age, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }

    if (additionalData.DOB) {
      const calc = calculateAge(additionalData.DOB);
      if (typeof calc === 'number' && !Number.isNaN(calc)) return calc;
    }

    return null;
  }, [additionalData]);

  // Only show if > 18, otherwise null
  const derivedAge =
    numericAge && numericAge > 18 ? `${numericAge} years old` : null;

  // ---------- Derived metrics ----------
  const metrics = profileData
    ? [
        {
          label: 'Followers',
          value: profileData.followers.toLocaleString(),
        },
        {
          label: 'Engagement rate*',
          value: (() => {
            const engagementRate =
              parseFloat(profileData.engagementRate) * 100;
            if (Number.isNaN(engagementRate)) return '—';
            const lowerBound = Math.max(engagementRate - 0.2, 0.1).toFixed(2);
            const upperBound = (engagementRate + 0.5).toFixed(2);
            return `${lowerBound}%–${upperBound}%`;
          })(),
        },
        {
          label: 'Average likes',
          value: Math.round(profileData.avgLikes).toLocaleString(),
        },
        {
          label: 'Average comments',
          value: Math.round(profileData.avgComments).toLocaleString(),
        },
      ]
    : [];

  // Bots / fakes percentage
  const rawBots =
    additionalData && additionalData.BotsFakes != null
      ? parseFloat(additionalData.BotsFakes)
      : null;
  const botPercentage =
    rawBots != null && !Number.isNaN(rawBots) ? rawBots * 100 : null;

  const pieData =
    botPercentage != null
      ? [
          {
            name: '',
            population: botPercentage,
            color: '#f97373',
            legendFontColor: '#111827',
            legendFontSize: 12,
          },
          {
            name: '',
            population: 100 - botPercentage,
            color: '#22c55e',
            legendFontColor: '#111827',
            legendFontSize: 12,
          },
        ]
      : [];

  // Parse AudienceLocation `"United States 59.81%, United Kingdom 4.61%"`
  const audienceBreakdown = useMemo(() => {
    if (!additionalData || !additionalData.AudienceLocation) return [];
    return additionalData.AudienceLocation.split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((chunk) => {
        const match = chunk.match(/(.+?)\s+([\d.]+)%/);
        if (!match) return null;
        return {
          country: match[1].trim(),
          percent: parseFloat(match[2]),
        };
      })
      .filter(Boolean);
  }, [additionalData]);

  const audienceBarData =
    audienceBreakdown.length > 0
      ? {
          labels: audienceBreakdown.map((a) =>
            a.country.length > 9 ? `${a.country.slice(0, 8)}…` : a.country
          ),
          datasets: [
            {
              data: audienceBreakdown.map((a) => a.percent),
            },
          ],
        }
      : null;

  // ---------- Loading & empty states (no sidenav here, to keep it simple) ----------
  if (loading || loadingAdditionalData) {
    return (
      <View style={styles.root}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.shellRow}>
            <View style={styles.mainArea}>
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#111827" />
                <Text style={styles.loadingText}>Analysing creator…</Text>
              </View>
            </View>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  if (!profileData && !additionalData) {
    return (
      <View style={styles.root}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.shellRow}>
            <View style={styles.mainArea}>
              <ScrollView
                contentContainerStyle={[
                  styles.scrollContent,
                  { maxWidth: 600 },
                ]}
              >
                <View style={styles.pageHeaderRow}>
                  <View style={styles.pageHeaderLeft}>
                    <TouchableOpacity
                      style={styles.backButton}
                      onPress={handleBackPress}
                    >
                      <Ionicons
                        name="arrow-back"
                        size={20}
                        color="#111827"
                      />
                    </TouchableOpacity>
                    <View>
                      <Text style={styles.pageTitle}>Creator insight</Text>
                      <Text style={styles.pageSubtitle}>
                        @{PlatformUsername}
                      </Text>
                    </View>
                  </View>
                </View>

                <View style={styles.noDataCard}>
                  <Ionicons
                    name="alert-circle-outline"
                    size={28}
                    color="#9ca3af"
                  />
                  <Text style={styles.noDataTitle}>
                    No data available for this creator
                  </Text>
                  <Text style={styles.noDataSubtitle}>
                    We couldn’t retrieve analytics for this profile right now.
                    Try again later or open their Instagram profile directly.
                  </Text>
                  <TouchableOpacity
                    style={styles.headerInstagramButton}
                    onPress={handleOpenInstagram}
                  >
                    <Ionicons
                      name="logo-instagram"
                      size={18}
                      color="#111827"
                    />
                    <Text style={styles.headerInstagramButtonText}>
                      Open Instagram
                    </Text>
                  </TouchableOpacity>
                </View>
              </ScrollView>
            </View>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  // ---------- Main render with SIDENAV ----------
  return (
    <View style={styles.root}>
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.shellRow}>
          {/* SIDE NAV */}
          <View style={styles.sideNav}>
            <LinearGradient
              colors={['#050509', '#050505', '#000000']}
              style={styles.sideNavInner}
            >
              {/* subtle glow blob */}
              <LinearGradient
                colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
                style={styles.sideNavGlow}
                start={{ x: 0.1, y: 0 }}
                end={{ x: 1, y: 1 }}
              />

              {/* TOP: Logo + wordmark */}
              <View style={styles.sideNavHeader}>
                <Image
                  source={{
                    uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
                  }}
                  style={styles.sideNavLogoImage}
                  resizeMode="contain"
                />
                <View>
                  <Text style={styles.logoWordmark}>
                    <Text style={{ fontWeight: '300' }}>collab</Text>
                    bay
                    <Text style={styles.logoDot}>.</Text>
                  </Text>
                  <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                </View>
              </View>

              {/* MAIN NAV SECTION */}
              <View style={styles.sideNavSection}>
                {/* Username pill */}
                <LinearGradient
                  colors={['#111111', '#2b2b2b']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.usernamePill}
                >
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={styles.usernamePillText}
                  >
                    {userData.Username
                      ? ` ${userData.Username}`
                      : 'Welcome Back'}
                  </Text>
                </LinearGradient>

                {/* Home → MyPanel */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('MyPanel')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="home-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Home</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Offers → MyOffers */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('MyOffers')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="pricetags-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Offers</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Calendar → MyCalendar (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab('https://collabbayadmin.com/subscribe');
                    } else {
                      navigation.navigate('MyCalendar');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="calendar-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Calendar</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Browse Creators → BrowseCreators */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('BrowseCreators')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="people-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Browse Creators</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* Activity → ActivityFeed (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab('https://collabbayadmin.com/subscribe');
                    } else {
                      navigation.navigate('ActivityFeed');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="chatbubbles-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Activity</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Account → BusinessAccountPage */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('BusinessAccountPage')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="person-circle-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Account</Text>
                    </View>
                  </View>
                </TouchableOpacity>
              </View>

              {/* MIDDLE SECTION: Peer Map + Subscribe CTA */}
              <View style={styles.sideNavSection}>
                {/* Peer Map */}
                <TouchableOpacity
                  style={styles.peerMapCard}
                  activeOpacity={0.9}
                  onPress={() => openInSameTab('/venues')}
                >
                  <LinearGradient
                    colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
                    style={styles.peerMapInner}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <View style={styles.peerMapContent}>
                      <Ionicons
                        name="map-outline"
                        size={18}
                        color="#FDF05D"
                        style={{ marginRight: 8 }}
                      />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.peerMapTitle}>Peer Map</Text>
                        <Text style={styles.peerMapSubtitle}>
                          View 100+ venues on Collabbay
                        </Text>
                      </View>
                    </View>
                  </LinearGradient>
                </TouchableOpacity>

                {/* Subscribe CTA — only if NOT subscriber */}
                {!isSubscriber && (
                  <TouchableOpacity
                    style={styles.subscribeCard}
                    activeOpacity={0.9}
                    onPress={() =>
                      openInSameTab('https://collabbayadmin.com/subscribe')
                    }
                  >
                    <LinearGradient
                      colors={['#FDF05D', '#fbbf24']}
                      style={styles.subscribeGradient}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                    >
                      <View style={styles.subscribeContent}>
                        <Ionicons
                          name="rocket-outline"
                          size={20}
                          color="#000"
                          style={{ marginRight: 8 }}
                        />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.subscribeTitle}>
                            Subscribe & unlock
                          </Text>
                          <Text style={styles.subscribeSubtitle}>
                            Unlock Activity & Calendar, plus more.
                          </Text>
                        </View>
                      </View>
                    </LinearGradient>
                  </TouchableOpacity>
                )}
              </View>

              {/* BOTTOM: FAQ + Help */}
              <View style={styles.sideNavBottom}>
                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() =>
                    openInSameTab('https://collabbayadmin.com/FAQs')
                  }
                >
                  <Ionicons
                    name="help-circle-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>FAQs</Text>
                    <Text style={styles.bottomLinkCaption}>
                      Common questions answered
                    </Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() =>
                    Linking.openURL('mailto:info@collab-bay.com')
                  }
                >
                  <Ionicons
                    name="headset-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>Help</Text>
                    <Text style={styles.bottomLinkCaption}>
                      We&apos;re here 24/7
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>

          {/* MAIN AREA */}
          <View style={styles.mainArea}>
            <ScrollView
              contentContainerStyle={[
                styles.scrollContent,
                { width: contentWidth },
              ]}
            >
              {/* Header */}
              <View style={styles.pageHeaderRow}>
                <View style={styles.pageHeaderLeft}>
                  <TouchableOpacity
                    style={styles.backButton}
                    onPress={handleBackPress}
                  >
                    <Ionicons
                      name="arrow-back"
                      size={20}
                      color="#111827"
                    />
                  </TouchableOpacity>
                  <View>
                    <Text style={styles.pageTitle}>Creator insight</Text>
                    <Text style={styles.pageSubtitle}>
                      @{profileData?.instagramName || PlatformUsername}
                    </Text>
                  </View>
                </View>

                <TouchableOpacity
                  style={styles.headerInstagramButton}
                  onPress={handleOpenInstagram}
                >
                  <Ionicons
                    name="logo-instagram"
                    size={18}
                    color="#111827"
                  />
                  <Text style={styles.headerInstagramButtonText}>
                    Open Instagram
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Two-column layout */}
              <View
                style={[
                  styles.gridRow,
                  !isLargeLayout && styles.gridRowStacked,
                ]}
              >
                {/* LEFT: Profile + metrics + audience */}
                <View
                  style={[
                    styles.leftColumn,
                    !isLargeLayout && { marginRight: 0 },
                  ]}
                >
                  {/* Profile hero card (bigger) */}
                  <View style={styles.profileCard}>
                    <View style={styles.profileTopRow}>
                      <View style={styles.profileAvatarWrapper}>
                        {additionalData?.ProfileImage ? (
                          <Image
                            source={{ uri: additionalData.ProfileImage }}
                            style={styles.profileAvatar}
                          />
                        ) : (
                          <Text style={styles.profileAvatarInitial}>
                            {profileData?.instagramName
                              ? profileData.instagramName
                                  .charAt(0)
                                  .toUpperCase()
                              : PlatformUsername.charAt(0).toUpperCase()}
                          </Text>
                        )}
                      </View>

                      <View style={{ flex: 1, marginLeft: 16 }}>
                        <Text style={styles.profileHandle}>
                          @{profileData?.instagramName || PlatformUsername}
                        </Text>

                        {additionalData?.ContentCategories && (
                          <View style={styles.categoryPill}>
                            <Text style={styles.categoryPillText}>
                              {additionalData.ContentCategories} creator
                            </Text>
                          </View>
                        )}

                        {derivedAge && (
                          <Text style={styles.profileMetaText}>
                            {derivedAge}
                          </Text>
                        )}
                      </View>

                      <TouchableOpacity
                        style={styles.profileInstagramButton}
                        onPress={handleOpenInstagram}
                      >
                        <Ionicons
                          name="logo-instagram"
                          size={18}
                          color="#111827"
                        />
                        <Text style={styles.profileInstagramButtonText}>
                          Profile
                        </Text>
                      </TouchableOpacity>
                    </View>

                    <Text style={styles.profileBio}>
                      {additionalData?.Interests ||
                        profileData?.description ||
                        'No bio information available.'}
                    </Text>
                  </View>

                  {/* Core metrics */}
                  {metrics.length > 0 && (
                    <View style={styles.metricsCard}>
                      <Text style={styles.sectionTitle}>Core metrics</Text>
                      <Text style={styles.sectionSubtitle}>
                        High-level performance based on recent posts.
                      </Text>
                      <View style={styles.metricsContainer}>
                        {metrics.map((metric, index) => (
                          <View key={index} style={styles.metricCard}>
                            <Text style={styles.metricValue}>
                              {metric.value}
                            </Text>
                            <Text style={styles.metricLabel}>
                              {metric.label}
                            </Text>
                          </View>
                        ))}
                      </View>
                      <Text style={styles.metricsFootnote}>
                        *Engagement rate is presented as an estimated band
                        rather than a single point.
                      </Text>
                    </View>
                  )}

                  {/* Audience locations */}
                  {audienceBarData && (
                    <View style={styles.audienceCard}>
                      <Text style={styles.sectionTitle}>
                        Audience snapshot
                      </Text>
                      <Text style={styles.sectionSubtitle}>
                        Top locations by follower percentage.
                      </Text>

                      <BarChart
                        data={audienceBarData}
                        width={chartWidth}
                        height={220}
                        fromZero
                        chartConfig={{
                          backgroundGradientFrom: 'transparent',
                          backgroundGradientTo: 'transparent',
                          decimalPlaces: 1,
                          color: (opacity = 1) =>
                            `rgba(17, 24, 39, ${opacity})`,
                          labelColor: (opacity = 1) =>
                            `rgba(107, 114, 128, ${opacity})`,
                          propsForBackgroundLines: {
                            strokeDasharray: '3',
                            stroke: '#e5e7eb',
                          },
                          barPercentage: 0.6,
                        }}
                        style={styles.barChart}
                      />

                      <View style={styles.audienceTagsRow}>
                        {audienceBreakdown.map((a, idx) => (
                          <View key={idx} style={styles.audienceTag}>
                            <Text style={styles.audienceTagText}>
                              {a.country} · {a.percent.toFixed(1)}%
                            </Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  )}
                </View>

                {/* RIGHT: Quick facts + fake followers */}
                <View style={styles.rightColumn}>
                  {additionalData && (
                    <View style={styles.quickFactsCard}>
                      <Text style={styles.sectionTitle}>Quick facts</Text>

                      {derivedAge && (
                        <View style={styles.quickFactRow}>
                          <Text style={styles.quickFactLabel}>Age</Text>
                          <Text style={styles.quickFactValue}>
                            {derivedAge}
                          </Text>
                        </View>
                      )}

                      {additionalData.ContentCategories && (
                        <View style={styles.quickFactRow}>
                          <Text style={styles.quickFactLabel}>
                            Primary niche
                          </Text>
                          <Text style={styles.quickFactValue}>
                            {additionalData.ContentCategories}
                          </Text>
                        </View>
                      )}

                      {additionalData.AudienceLocation && (
                        <View style={styles.quickFactRow}>
                          <Text style={styles.quickFactLabel}>
                            Top audience
                          </Text>
                          <Text style={styles.quickFactValue}>
                            {audienceBreakdown[0]
                              ? `${audienceBreakdown[0].country} · ${audienceBreakdown[0].percent.toFixed(
                                  1
                                )}%`
                              : additionalData.AudienceLocation}
                          </Text>
                        </View>
                      )}

                      {botPercentage != null && (
                        <View style={styles.quickFactRow}>
                          <Text style={styles.quickFactLabel}>
                            Est. fake / low-quality
                          </Text>
                          <Text style={styles.quickFactValue}>
                            {botPercentage.toFixed(1)}%
                          </Text>
                        </View>
                      )}
                    </View>
                  )}

                  {botPercentage != null && (
                    <View style={styles.pieChartCard}>
                      <Text style={styles.sectionTitle}>
                        Fake follower analysis
                      </Text>
                      <Text style={styles.sectionSubtitle}>
                        Estimated distribution of bots/fakes vs organic
                        followers.
                      </Text>

                      <PieChart
                        data={pieData}
                        width={chartWidth}
                        height={220}
                        chartConfig={{
                          color: (opacity = 1) =>
                            `rgba(0, 0, 0, ${opacity})`,
                        }}
                        accessor="population"
                        backgroundColor="transparent"
                        paddingLeft="16"
                        absolute
                        hasLegend={false}
                      />

                      <Text style={styles.pieChartDescription}>
                        A fake/low-quality follower range around 20–30% is
                        common for many creators. Combine this with content
                        quality, total reach, and brand fit when deciding on
                        collaborations.
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            </ScrollView>
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  safeArea: {
    flex: 1,
  },

  // APP SHELL
  shellRow: {
    flex: 1,
    flexDirection: 'row',
  },

  mainArea: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },

  scrollContent: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    alignItems: 'center',
    alignSelf: 'center',
  },

  // SIDE NAV
  sideNav: {
    width: 250,
    backgroundColor: '#000',
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  sideNavGlow: {
    position: 'absolute',
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '800',
  },
  logoDesktopTag: {
    fontSize: 9,
    color: '#b3b3b3',
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'left',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  navText: {
    color: '#f5f5f5',
    fontSize: 14,
    fontWeight: '500',
  },
  lockIcon: {
    marginLeft: 6,
  },

  // Peer Map card
  peerMapCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  peerMapTitle: {
    color: '#f5f5f5',
    fontSize: 13,
    fontWeight: '600',
  },
  peerMapSubtitle: {
    color: '#b3b3b3',
    fontSize: 11,
    marginTop: 2,
  },

  // Subscribe CTA
  subscribeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: '#1f2933',
    marginTop: 2,
  },

  // BOTTOM LINKS
  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: '#f5f5f5',
    fontWeight: '600',
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: '#b3b3b3',
    marginTop: 2,
  },

  // Header
  pageHeaderRow: {
    width: '100%',
    maxWidth: 1100,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  pageHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    width: 34,
    height: 34,
    borderRadius: 999,
    backgroundColor: '#e5e7eb',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  pageTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
  },
  pageSubtitle: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  headerInstagramButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fdf05d',
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#eab308',
  },
  headerInstagramButtonText: {
    marginLeft: 6,
    fontSize: 12,
    fontWeight: '600',
    color: '#111827',
  },

  // Layout
  gridRow: {
    width: '100%',
    maxWidth: 1100,
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 4,
  },
  gridRowStacked: {
    flexDirection: 'column',
  },
  leftColumn: {
    flex: 2,
    marginRight: 16,
  },
  rightColumn: {
    flex: 1,
  },

  // Profile card (bigger)
  profileCard: {
    backgroundColor: '#ffffff',
    borderRadius: 18,
    padding: 18,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 12,
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    elevation: 1,
  },
  profileTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  profileAvatarWrapper: {
    width: 82,
    height: 82,
    borderRadius: 999,
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  profileAvatar: {
    width: '100%',
    height: '100%',
    borderRadius: 999,
  },
  profileAvatarInitial: {
    fontSize: 30,
    fontWeight: '700',
    color: '#111827',
  },
  profileHandle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
  },
  profileMetaText: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  categoryPill: {
    alignSelf: 'flex-start',
    marginTop: 4,
    paddingVertical: 3,
    paddingHorizontal: 9,
    borderRadius: 999,
    backgroundColor: '#f3f4f6',
  },
  categoryPillText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#111827',
  },
  profileInstagramButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 7,
    paddingHorizontal: 11,
    borderRadius: 999,
    backgroundColor: '#f3f4f6',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  profileInstagramButtonText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#111827',
    marginLeft: 4,
  },
  profileBio: {
    fontSize: 13,
    color: '#4b5563',
    marginTop: 2,
  },

  // Metrics
  metricsCard: {
    backgroundColor: '#ffffff',
    borderRadius: 18,
    padding: 14,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
  },
  sectionSubtitle: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 2,
  },
  metricsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  metricCard: {
    width: '48%',
    backgroundColor: '#fdf05d',
    borderRadius: 14,
    paddingVertical: 14,
    paddingHorizontal: 10,
    marginBottom: 10,
    alignItems: 'flex-start',
  },
  metricValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  metricLabel: {
    fontSize: 11,
    color: '#43302a',
    marginTop: 4,
    fontWeight: '500',
  },
  metricsFootnote: {
    fontSize: 10,
    color: '#9ca3af',
    marginTop: 6,
  },

  // Audience card
  audienceCard: {
    backgroundColor: '#ffffff',
    borderRadius: 18,
    padding: 14,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 14,
  },
  barChart: {
    marginTop: 6,
    borderRadius: 12,
  },
  audienceTagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
  },
  audienceTag: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 999,
    backgroundColor: '#f3f4f6',
    marginRight: 6,
    marginBottom: 6,
  },
  audienceTagText: {
    fontSize: 11,
    color: '#374151',
  },

  // Quick facts
  quickFactsCard: {
    backgroundColor: '#ffffff',
    borderRadius: 18,
    padding: 14,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 12,
  },
  quickFactRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  quickFactLabel: {
    fontSize: 11,
    color: '#6b7280',
  },
  quickFactValue: {
    fontSize: 12,
    color: '#111827',
    fontWeight: '600',
    maxWidth: '60%',
    textAlign: 'right',
  },

  // Pie chart card
  pieChartCard: {
    backgroundColor: '#ffffff',
    borderRadius: 18,
    padding: 14,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 18,
  },
  pieChartDescription: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 8,
  },

  // Loading & empty state
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 14,
    color: '#111827',
  },
  noDataCard: {
    width: '100%',
    backgroundColor: '#ffffff',
    borderRadius: 18,
    padding: 18,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    alignItems: 'center',
    marginTop: 20,
  },
  noDataTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
    marginTop: 8,
  },
  noDataSubtitle: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
    textAlign: 'center',
  },
});

export default InfluencerAnalysis;
